import React from 'react'
import {
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Skeleton,
  Typography,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { makeStyles } from '@mui/styles'
import { useNavigate } from 'react-router-dom'

import { withModal } from 'utils/providers/ModalProvider'

import { getDeclineCampaignOptions, rejectCampaign } from 'api/campaigns'

import modals from 'constants/modals'

import { BaseDialog, FormikField, StyledButton } from 'shared'

import {
  BaseModalComponentType,
  ModalNameToModalMap,
} from 'components/modals/types'
import { toast } from 'react-toastify'
import { Form, Formik } from 'formik'
import { useAnalytics } from 'utils/providers/AnalyticsProvider'
import { ANALYTICS_EVENT_NAMES } from 'constants/analytics'
import { validationSchema } from './config'

const useStyles = makeStyles(() => {
  return {
    dialog: {
      '& .base-dialog-header': {
        marginBottom: 16,
        '& h2': {
          fontSize: '24px',
          fontWeight: '700',
          lineHeight: '24px',
        },
      },
    },
  }
})

type DeclineSubmitProps = {
  comment: string | null
  declineOptionId: number | null
}

const DeclineInviteModal = ({
  isOpen,
  closeModal,
  payload,
}: BaseModalComponentType & {
  payload: ModalNameToModalMap['declineInvite']
}) => {
  const classes = useStyles()
  const { analyticsTrack } = useAnalytics()

  const { campaign } = payload || {}
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const { data: declineOptions, isLoading: isDeclineOptionsLoading } = useQuery(
    {
      queryKey: ['decline_campaign_options'],
      queryFn: () => getDeclineCampaignOptions(),
    }
  )

  const rejectCampaignMutation = useMutation({
    mutationFn: rejectCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries(['campaign'])
      toast.success('Campaign declined successfully')
      navigate('/campaigns')
      analyticsTrack(`${ANALYTICS_EVENT_NAMES.campaign}: Declined`, {
        campaignId: campaign?.id,
        campaignName: campaign?.name || '',
      })
      closeModal()
    },
  })

  const onSubmit = (values: DeclineSubmitProps) => {
    if (!campaign?.id) return
    rejectCampaignMutation.mutate({
      id: campaign.id,
      data: values,
    })
  }

  return (
    <BaseDialog
      open={isOpen}
      onClose={closeModal}
      title={`Are you sure you want to decline ${
        campaign?.name || ''
      } campaign?`}
      className={classes.dialog}
    >
      <Grid display="flex" flexDirection="column" gap="12px">
        <Typography>This action cannot be undone.</Typography>
        <Typography>
          Don’t worry—declining this campaign won’t affect your chances for
          future campaign opportunities with us!
        </Typography>
        <Typography>
          Please tell us why you're not interested in participating in this
          campaign:
        </Typography>
      </Grid>
      <Formik
        onSubmit={onSubmit}
        initialValues={{ comment: '', declineOptionId: 1 }}
        validationSchema={validationSchema}
      >
        {({ values, setFieldValue }) => {
          return (
            <Form>
              {isDeclineOptionsLoading ? (
                <Grid container flexDirection="column" gap="16px" mt={1}>
                  {Array(3)
                    .fill(0)
                    .map((_, index) => {
                      return (
                        <Skeleton
                          key={index}
                          sx={{ borderRadius: 1 }}
                          variant="rectangular"
                        />
                      )
                    })}
                </Grid>
              ) : (
                <>
                  {declineOptions?.map((option) => {
                    return (
                      <RadioGroup key={option.id}>
                        <FormControlLabel
                          id={String(option.id)}
                          value={option.id}
                          label={option.text}
                          control={<Radio color="mint" />}
                          checked={option.id == values.declineOptionId}
                          onChange={() =>
                            setFieldValue('declineOptionId', option.id)
                          }
                        />
                      </RadioGroup>
                    )
                  })}
                  <RadioGroup>
                    <FormControlLabel
                      id="other"
                      value={null}
                      label="Other (please specify):"
                      control={<Radio color="mint" />}
                      checked={values.declineOptionId === null}
                      onChange={() => setFieldValue('declineOptionId', null)}
                    />
                  </RadioGroup>
                </>
              )}

              <Grid mt={2}>
                <FormikField
                  rows={2}
                  multiline
                  name="comment"
                  placeholder="Comment"
                  value={values.comment}
                />
              </Grid>
              <Grid
                container
                justifyContent="flex-end"
                gap="16px"
                marginTop="24px"
              >
                <StyledButton
                  color="light"
                  variant="contained"
                  onClick={closeModal}
                >
                  Cancel
                </StyledButton>
                <StyledButton
                  color="error"
                  variant="contained"
                  type="submit"
                  disabled={isDeclineOptionsLoading}
                >
                  Decline campaign
                </StyledButton>
              </Grid>
            </Form>
          )
        }}
      </Formik>
    </BaseDialog>
  )
}

export default withModal(modals.declineInvite)(DeclineInviteModal)
